import { Box, Stack, useTheme } from "@mui/material";
import dynamic from "next/dynamic";

import dragAnimationData from "./drag-animation.json";
import type { AspectRatio } from "./helpers";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ScaleIn, ScaleOut } from "@/icons/outlined";

const Lottie = dynamic(() => import("react-lottie"), { ssr: false });

const dragAnimation = structuredClone(dragAnimationData);

export interface HoverOverlayProps {
  onReplace?: () => void;
  onDelete?: () => void;
  onFit?: () => void;
  showFitButton?: boolean;
  showDragText?: boolean;
  showCenterFitButton?: boolean;
  isFitMode?: boolean;
  aspectRatio?: AspectRatio | null;
  isVisible?: boolean;
  targetSize?: number;
  viewportSize?: number;
}

export const HoverOverlay = ({
  onReplace,
  onDelete,
  onFit,
  showFitButton = false,
  showDragText = false,
  showCenterFitButton = false,
  isFitMode = false,
  aspectRatio,
  isVisible = true,
  targetSize,
  viewportSize,
}: HoverOverlayProps) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  const isCompact =
    isSmallScreen ||
    (targetSize !== undefined && targetSize <= 400) ||
    (viewportSize !== undefined && viewportSize <= 300);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        borderRadius: "8px",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
        transition: "opacity 0.4s ease-in-out",
      }}
    >
      {/* Top left: Fit toggle button */}
      {showFitButton && onFit && (
        <DashboardButton
          variant="outlined"
          vibe="neutral"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onFit();
          }}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            backgroundColor: theme.palette.surface.neutral.white,
            minWidth: "auto",
            padding: 1,
            paddingBottom: 1,
          }}
        >
          <DashboardIcon size="small" vibe="inherit">
            {isFitMode ? <ScaleOut /> : <ScaleIn />}
          </DashboardIcon>
        </DashboardButton>
      )}

      {/* Top center: Drag to reposition */}
      {showDragText && (
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          sx={{
            position: "absolute",
            top: 12,
            left: showFitButton ? "calc(50% + 24px)" : "50%",
            transform: "translateX(-50%)",
          }}
        >
          <DashboardTypography
            variant={isCompact ? "body2" : "body1"}
            vibe="text-reverse-intense"
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("common", "dragToReposition")}
          </DashboardTypography>
          <Lottie
            options={{ animationData: dragAnimation, autoplay: true, loop: true }}
            style={{
              width: isCompact ? 28 : 40,
              height: isCompact ? 28 : 40,
              transform: aspectRatio === "portrait" ? "rotate(-90deg)" : undefined,
            }}
          />
        </Stack>
      )}

      {/* Center: Action buttons */}
      <Stack direction="row" gap={1}>
        {showCenterFitButton && onFit && (
          <DashboardButton
            variant="outlined"
            vibe="neutral"
            onClick={(e) => {
              e.stopPropagation();
              onFit();
            }}
            sx={{ backgroundColor: theme.palette.surface.neutral.white }}
          >
            {isFitMode ? t("common", "fill") : t("common", "fit")}
          </DashboardButton>
        )}
        {!showCenterFitButton && onReplace && (
          <DashboardButton
            variant="outlined"
            vibe="neutral"
            onClick={(e) => {
              e.stopPropagation();
              onReplace();
            }}
            sx={{ backgroundColor: theme.palette.surface.neutral.white }}
          >
            {t("common", "replaceImage")}
          </DashboardButton>
        )}
        {onDelete && (
          <DashboardButton
            variant="outlined"
            vibe="neutral"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ backgroundColor: theme.palette.surface.neutral.white }}
          >
            {t("common", "deleteImage")}
          </DashboardButton>
        )}
      </Stack>
    </Box>
  );
};
